import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMovies } from "./redux/slices/moviesSlice";
import MovieList from "./components/MovieList";
import SeriesList from "./components/SeriesList";
import AnimeList from "./components/AnimeList";
import SearchBar from "./components/SearchBar";
import SplashScreen from "./components/SplashScreen";
import Footer from "./components/Footer";
import logoiii from "../src/assets/logoiii.png";
import MovieSlider from "./components/MovieSlider/MovieSlider";
import AnimeSlider from "./components/AnimeSlider/AnimeSlider";
import SeriesSlider from "./components/SeriesSlider/SeriesSlider";
import { getAnimes } from "./services/animeService";
import { getAllSeries } from "./services/seriesService";
import "./index.css";

const App = () => {
  const dispatch = useDispatch();
  const reduxMovies = useSelector((state) => state.movies.movies);

  const [filteredMovies, setFilteredMovies] = useState([]);
  const [sliderMovies, setSliderMovies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("");
  const [showSplash, setShowSplash] = useState(true);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("movies");
  const [animes, setAnimes] = useState([]);
  const [filteredAnimes, setFilteredAnimes] = useState([]);
  const [loadingAnimes, setLoadingAnimes] = useState(false);
  const [animeGenres, setAnimeGenres] = useState([]);
  const [selectedAnimeGenre, setSelectedAnimeGenre] = useState([]);
  const [sliderAnimes, setSliderAnimes] = useState([]);

  // Add series state variables
  const [series, setSeries] = useState([]);
  const [filteredSeries, setFilteredSeries] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [seriesGenres, setSeriesGenres] = useState([]);
  const [selectedSeriesGenre, setSelectedSeriesGenre] = useState("");
  const [sliderSeries, setSliderSeries] = useState([]);

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Verificar si es la primera visita
    const hasVisitedBefore = localStorage.getItem("hasVisitedEpicMovie");
    console.log("SplashScreen - Estado:", { hasVisitedBefore, showSplash });

    if (!hasVisitedBefore) {
      // Primera visita, mostrar splash screen
      localStorage.setItem("hasVisitedEpicMovie", "true");

      // Mostrar splash screen solo por un tiempo determinado
      console.log("SplashScreen - Configurando timer para primera visita");

      // Timer de seguridad para ocultar el splash screen después de 3.5 segundos
      const splashTimer = setTimeout(() => {
        console.log("SplashScreen - Ocultando después de timeout");
        setShowSplash(false);
      }, 3500);

      // Cleanup: limpiar el timer si el componente se desmonta antes de que termine
      return () => {
        clearTimeout(splashTimer);
        console.log("SplashScreen - Timer limpiado por cleanup");
      };
    } else {
      // No es primera visita, ocultar splash inmediatamente
      console.log("SplashScreen - No es primera visita, ocultando splash");
      setShowSplash(false);
    }
  }, []);

  // Efecto separado para cargar películas
  useEffect(() => {
    // Cargar películas desde Redux
    console.log("Iniciando carga de películas");
    dispatch(fetchMovies());
  }, [dispatch]);

  // Efecto separado para actualizar estado cuando las películas se cargan
  useEffect(() => {
    console.log("Películas cargadas en Redux:", reduxMovies.length);

    if (reduxMovies.length > 0) {
      // Ya tenemos películas, quitar el estado de carga
      setLoading(false);

      // Actualizar películas filtradas (todas al inicio)
      setFilteredMovies(reduxMovies);

      // Seleccionar películas más recientes para el slider inicial
      const sortedByRecent = [...reduxMovies].sort((a, b) => {
        // Si releaseYear no está disponible, usar el ID como fallback
        if (!a.releaseYear && !b.releaseYear) return b.id - a.id;
        if (!a.releaseYear) return 1;
        if (!b.releaseYear) return -1;
        return b.releaseYear - a.releaseYear;
      });

      const featuredMovies =
        sortedByRecent.length > 10
          ? sortedByRecent.slice(0, 10) // Tomar las 10 películas más recientes para el slider
          : sortedByRecent;
      setSliderMovies(featuredMovies);
    }
  }, [reduxMovies]); // Esta dependencia asegura que se ejecute cuando las películas están disponibles

  useEffect(() => {
    setFilteredMovies(reduxMovies);

    // Actualizar también películas del slider cuando cambia el catálogo completo
    if (selectedGenre === "") {
      const sortedByRecent = [...reduxMovies].sort((a, b) => {
        if (!a.releaseYear && !b.releaseYear) return b.id - a.id;
        if (!a.releaseYear) return 1;
        if (!b.releaseYear) return -1;
        return b.releaseYear - a.releaseYear;
      });

      const featuredMovies =
        sortedByRecent.length > 10
          ? sortedByRecent.slice(0, 10)
          : sortedByRecent;
      setSliderMovies(featuredMovies);
    }
  }, [reduxMovies]);

  useEffect(() => {
    // Fetch animes when active tab is anime
    if (activeTab === "anime" && animes.length === 0) {
      const fetchAnimes = async () => {
        try {
          setLoadingAnimes(true);
          const response = await getAnimes(1, 10);
          if (response.success) {
            setAnimes(response.data);
            setFilteredAnimes(response.data);

            // Extraer géneros únicos de animes
            const uniqueGenres = new Set();
            response.data.forEach((anime) => {
              if (typeof anime.genre === "string") {
                anime.genre
                  .split(",")
                  .forEach((g) => uniqueGenres.add(g.trim()));
              } else if (Array.isArray(anime.genre)) {
                anime.genre.forEach((g) => uniqueGenres.add(g.trim()));
              }
            });

            setAnimeGenres(Array.from(uniqueGenres).sort());
          }
        } catch (error) {
          console.error("Error al cargar animes:", error);
        } finally {
          setLoadingAnimes(false);
        }
      };

      fetchAnimes();
    }
  }, [activeTab, animes.length]);

  useEffect(() => {
    if (animes.length > 0) {
      console.log("Animes cargados:", animes.length);

      // Seleccionar animes más recientes para el slider inicial
      const sortedByRecent = [...animes].sort((a, b) => {
        // Si releaseYear no está disponible, usar el ID como fallback
        if (!a.releaseYear && !b.releaseYear) return b.id - a.id;
        if (!a.releaseYear) return 1;
        if (!b.releaseYear) return -1;
        return b.releaseYear - a.releaseYear;
      });

      const featuredAnimes =
        sortedByRecent.length > 10
          ? sortedByRecent.slice(0, 10) // Tomar los 10 animes más recientes para el slider
          : sortedByRecent;
      setSliderAnimes(featuredAnimes);
    }
  }, [animes]);

  // Add effect for fetching series when Series tab is active
  useEffect(() => {
    // Fetch series when active tab is series
    if (activeTab === "series" && series.length === 0) {
      const fetchSeries = async () => {
        try {
          setLoadingSeries(true);
          console.log("Iniciando carga de series...");

          // Get first page with a larger page size to have enough for the slider
          const response = await getAllSeries(1, 20);

          if (response && response.data) {
            console.log("Series cargadas:", response.data.length);
            setSeries(response.data);
            setFilteredSeries(response.data);

            // Extract unique genres from series
            // Extraer géneros únicos de series
            const uniqueGenres = new Set();
            response.data.forEach((seriesItem) => {
              const raw = seriesItem.genres ?? seriesItem.genre; // <- acepta ambos
              if (typeof raw === "string") {
                raw.split(",").forEach((g) => uniqueGenres.add(g.trim()));
              } else if (Array.isArray(raw)) {
                raw.forEach((g) => uniqueGenres.add(String(g).trim()));
              }
            });
            setSeriesGenres(Array.from(uniqueGenres).sort());

            // const uniqueGenres = new Set();
            // response.data.forEach(seriesItem => {
            //   if (typeof seriesItem.genres === 'string') {
            //     seriesItem.genres.split(',').forEach(g => uniqueGenres.add(g.trim()));
            //   } else if (Array.isArray(seriesItem.genres)) {
            //     seriesItem.genres.forEach(g => uniqueGenres.add(g.trim()));
            //   }
            // });

            // setSeriesGenres(Array.from(uniqueGenres).sort());

            // Select series for the slider (most recent ones)
            const sortedByRecent = [...response.data].sort((a, b) => {
              if (!a.releaseYear && !b.releaseYear) return b.id - a.id;
              if (!a.releaseYear) return 1;
              if (!b.releaseYear) return -1;
              return b.releaseYear - a.releaseYear;
            });

            const featuredSeries =
              sortedByRecent.length > 10
                ? sortedByRecent.slice(0, 10)
                : sortedByRecent;

            setSliderSeries(featuredSeries);
          }
        } catch (error) {
          console.error("Error al cargar series:", error);
        } finally {
          setLoadingSeries(false);
        }
      };

      fetchSeries();
    }
  }, [activeTab, series.length]);

  const handleSearch = (term) => {
    setSearchTerm(term);

    if (term === "") {
      // Si se borra la búsqueda, mostrar todas las películas y restaurar el slider original
      if (selectedGenre === "") {
        setFilteredMovies(reduxMovies);
        const sortedByRecent = [...reduxMovies].sort((a, b) => {
          if (!a.releaseYear && !b.releaseYear) return b.id - a.id;
          if (!a.releaseYear) return 1;
          if (!b.releaseYear) return -1;
          return b.releaseYear - a.releaseYear;
        });

        const featuredMovies =
          sortedByRecent.length > 10
            ? sortedByRecent.slice(0, 10)
            : sortedByRecent;
        setSliderMovies(featuredMovies);
      } else {
        // Si hay un género seleccionado, mantener el filtro por género
        handleGenreSelect(selectedGenre);
      }
    } else {
      // Filtrar por término de búsqueda
      const filtered = reduxMovies.filter((movie) =>
        movie.name.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredMovies(filtered);
      setSliderMovies(filtered);
    }
  };

  const handleGenreSelect = (genre) => {
    setSelectedGenre(genre);

    console.log("Género seleccionado:", genre);

    if (genre) {
      const genreLower = genre.toLowerCase();

      console.log(
        "Datos de géneros disponibles:",
        reduxMovies.map((m) => ({
          id: m.id,
          name: m.name,
          genre: m.genre,
        }))
      );

      const filtered = reduxMovies.filter((movie) => {
        if (!movie.genre) return false;

        if (typeof movie.genre === "string") {
          return movie.genre.toLowerCase().includes(genreLower);
        }

        if (Array.isArray(movie.genre)) {
          return movie.genre.some((g) => g.toLowerCase().includes(genreLower));
        }

        return false;
      });

      console.log(
        `Películas filtradas para ${genre}:`,
        filtered.length,
        filtered.map((m) => m.name)
      );
      setFilteredMovies(filtered);
      setSliderMovies(filtered); // Actualizar también el slider con las películas filtradas
    } else {
      // Si no hay género seleccionado, mostrar todas las películas
      setFilteredMovies(reduxMovies);

      // Para el slider, mostrar solo una selección destacada
      const sortedByRecent = [...reduxMovies].sort((a, b) => {
        if (!a.releaseYear && !b.releaseYear) return b.id - a.id;
        if (!a.releaseYear) return 1;
        if (!b.releaseYear) return -1;
        return b.releaseYear - a.releaseYear;
      });

      const featuredMovies =
        sortedByRecent.length > 10
          ? sortedByRecent.slice(0, 10)
          : sortedByRecent;
      setSliderMovies(featuredMovies);
    }
  };

  // Filtrar animes por género
  const handleAnimeGenreSelect = (genre) => {
    setSelectedAnimeGenre(genre);

    if (!genre) {
      setFilteredAnimes(animes); // Mostrar todos los animes si no hay género seleccionado
      return;
    }

    const filtered = animes.filter((anime) => {
      if (typeof anime.genre === "string") {
        return anime.genre.toLowerCase().includes(genre.toLowerCase());
      }
      if (Array.isArray(anime.genre)) {
        return anime.genre.some((g) =>
          g.toLowerCase().includes(genre.toLowerCase())
        );
      }
      return false;
    });

    setFilteredAnimes(filtered);
  };

  // Add handler for series genre selection
  const handleSeriesGenreSelect = (genre) => {
    setSelectedSeriesGenre(genre);

    if (!genre) {
      setFilteredSeries(series);
      return;
    }

    const needle = genre.toLowerCase();

    const filtered = series.filter((item) => {
      const raw = item.genres ?? item.genre;
      if (!raw) return false;

      if (typeof raw === "string") {
        return raw.toLowerCase().includes(needle);
      }

      if (Array.isArray(raw)) {
        return raw.some((g) => {
          if (typeof g === "string") return g.toLowerCase().includes(needle);
          // soporta array de objetos (TMDB-like)
          const name = (g?.name || g?.title || g?.label || "").toLowerCase();
          return name.includes(needle);
        });
      }

      return false;
    });

    setFilteredSeries(filtered);
  };

  // const handleSeriesGenreSelect = (genre) => {
  //   setSelectedSeriesGenre(genre);

  //   if (!genre) {
  //     setFilteredSeries(series);
  //     return;
  //   }

  //   const genreLower = genre.toLowerCase();
  //   const filtered = series.filter((seriesItem) => {
  //     if (typeof seriesItem.genres === "string") {
  //       return seriesItem.genres.toLowerCase().includes(genreLower);
  //     }
  //     if (Array.isArray(seriesItem.genres)) {
  //       return seriesItem.genres.some((g) =>
  //         g.toLowerCase().includes(genreLower)
  //       );
  //     }
  //     return false;
  //   });

  //   setFilteredSeries(filtered);
  // };

  const genres = [
    "Accion",
    "Aventura",
    "Ciencia Ficcion",
    "Comedia",
    "Crimen",
    "Drama",
    "Misterio",
    "Musical",
    "Romance",
    "Terror",
    "Western",
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="flex flex-col min-h-screen bg-black">
      {showSplash ? (
        <SplashScreen />
      ) : (
        <>
          <div className="flex justify-center mt-16 mb-1 px-4">
            <SearchBar onSearch={handleSearch} selectedCategory="my_movies" />
          </div>

          <div className="flex justify-center mb-4">
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <button
                className={`px-6 py-2 ${
                  activeTab === "movies"
                    ? "bg-blue-600 text-white"
                    : "text-gray-300 hover:bg-gray-700"
                }`}
                onClick={() => handleTabChange("movies")}
              >
                Películas
              </button>
              <button
                className={`px-6 py-2 ${
                  activeTab === "series"
                    ? "bg-blue-600 text-white"
                    : "text-gray-300 hover:bg-gray-700"
                }`}
                onClick={() => handleTabChange("series")}
              >
                Series
              </button>
              <button
                className={`px-6 py-2 ${
                  activeTab === "anime"
                    ? "bg-blue-600 text-white"
                    : "text-gray-300 hover:bg-gray-700"
                }`}
                onClick={() => handleTabChange("anime")}
              >
                Anime
              </button>
            </div>
          </div>

          {activeTab === "movies" && (
            <div className="p-4">
              <select
                onChange={(e) => handleGenreSelect(e.target.value)}
                className="bg-gray-800 text-white p-2 rounded-lg"
              >
                <option value="">Seleccionar Género</option>
                {genres.map((genre) => (
                  <option key={genre} value={genre}>
                    {genre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {activeTab === "movies" && !loading && sliderMovies.length > 0 && (
            <div className="mt-4">
              <MovieSlider movies={sliderMovies} />
            </div>
          )}

          {activeTab === "movies" && sliderMovies.length === 0 && !loading && (
            <div className="mt-4 text-center text-white">
              No hay películas que coincidan
            </div>
          )}

          {activeTab === "anime" && (
            <div className="p-4">
              <select
                onChange={(e) => handleAnimeGenreSelect(e.target.value)}
                className="bg-gray-800 text-white p-2 rounded-lg"
              >
                <option value="">Seleccionar Género</option>
                {animeGenres.map((genre) => (
                  <option key={genre} value={genre}>
                    {genre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {activeTab === "anime" &&
            !loadingAnimes &&
            sliderAnimes.length > 0 && (
              <div className="mt-4">
                <AnimeSlider animes={sliderAnimes} />
              </div>
            )}

          {activeTab === "anime" &&
            sliderAnimes.length === 0 &&
            !loadingAnimes && (
              <div className="mt-4 text-center text-white">
                No hay animes que coincidan
              </div>
            )}

          {/* Add series genre filter */}
          {activeTab === "series" && (
            <div className="p-4">
              <select
                onChange={(e) => handleSeriesGenreSelect(e.target.value)}
                className="bg-gray-800 text-white p-2 rounded-lg"
              >
                <option value="">Seleccionar Género</option>
                {seriesGenres.map((genre) => (
                  <option key={genre} value={genre}>
                    {genre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Add series slider */}
          {activeTab === "series" &&
            !loadingSeries &&
            sliderSeries.length > 0 && (
              <div className="mt-4">
                <SeriesSlider series={sliderSeries} />
              </div>
            )}

          {activeTab === "series" &&
            sliderSeries.length === 0 &&
            !loadingSeries && (
              <div className="mt-4 text-center text-white">
                No hay series que coincidan
              </div>
            )}

          <div className="flex-grow">
            {loading ? (
              <img
                src={logoiii}
                alt="Logo de EPICMOVIE"
                className="w-24 h-24 mb-1 mx-auto"
              />
            ) : (
              <>
                {activeTab === "movies" && (
                  <MovieList movies={filteredMovies} />
                )}
                {activeTab === "series" && (
                  <SeriesList
                    searchTerm={searchTerm}
                    genreFilter={selectedSeriesGenre}
                    series={filteredSeries}
                  />
                )}
                {activeTab === "anime" && (
                  <AnimeList searchTerm={searchTerm} animes={filteredAnimes} />
                )}
              </>
            )}
          </div>

          <Footer />
        </>
      )}
    </div>
  );
};

export default App;
